import { useTranslation } from "react-i18next";

import { useAppState } from "../../../../contexts/App";
import CheckIcon from "../../../../icons/Check";
import { classNames } from "../../../../utils/styles";
import { Steps, Wrapper } from "./styles";

// Every screen of creating a Keepo, up to publishing it
const STEPS = ["name", "start", "template", "sections", "style", "address", "ready", "pages"] as const;

export type CreationStep = (typeof STEPS)[number];

type Group = { key: string; steps: CreationStep[] };

// The bar shows 4 groups of those screens; it moves on only when a whole group is done
const CREATION_GROUPS: Group[] = [
  { key: "name", steps: ["name"] },
  { key: "start", steps: ["start", "template"] },
  // the Keepo's pages (the home among them), each one's sections, and its style
  { key: "customize", steps: ["pages", "sections", "style"] },
  { key: "publish", steps: ["address", "ready"] },
];

// Editing a published Keepo starts at its pages (its address can't change): the pages and each one's sections,
// the style, publishing
const EDITING_GROUPS: Group[] = [
  { key: "pages", steps: ["pages", "sections"] },
  { key: "style", steps: ["style"] },
  { key: "publish", steps: ["ready"] },
];

type Props = {
  step: CreationStep;
};

/**
 * The Keepo creation steps on top of its screens: done ones checked, the current one highlighted.
 * Once the Keepo is published, editing it shows its own (shorter) steps.
 */
const CreationProgress = ({ step }: Props) => {
  const { t } = useTranslation();
  const { appState } = useAppState();
  const prefix = "pages.editor.creationProgress";

  const groups = appState.published ? EDITING_GROUPS : CREATION_GROUPS;
  const current = groups.findIndex(group => group.steps.includes(step)) + 1;

  return (
    <Wrapper>
      <Steps aria-label={t(`${prefix}.counter`, { current, total: groups.length })}>
        {groups.map(({ key }, index) => {
          const number = index + 1;
          return (
            <li
              key={key}
              className={classNames({ current: number === current, done: number < current })}
              aria-current={number === current ? "step" : undefined}>
              <span className="dot">
                {number < current ? <CheckIcon width={10} height={10} color="currentColor" /> : number}
              </span>
              <span className="label">{t(`${prefix}.steps.${key}`)}</span>
            </li>
          );
        })}
      </Steps>
    </Wrapper>
  );
};

export default CreationProgress;
